import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { injectDispatch, provideDispatcher } from '@ngrx/signals/events';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ColumnSizes,
  DataSource,
  Pagination,
  PaginationValueObject,
  Sorting,
} from 'wflow-cdk';
import {
  FilterPanelComponent,
  IconsModule,
  PanelFilterState,
  SearchInputComponent,
  setColumnSizes,
  SmartTableModule,
  WfDialogService,
  WuiTooltip,
} from 'wflow-ui';
import { take } from 'rxjs/operators';
import { PEPPOL_STATUS_ACTIONS } from '../../const/peppol-actions.const';
import { PEPPOL_DIRECTION_CONFIG } from '../../const/peppol-direction.const';
import { PEPPOL_DOCUMENT_CONFIG } from '../../const/peppol-document.const';
import { PEPPOL_STATUS_CONFIG } from '../../const/peppol-status.const';
import { PeppolItem } from '../../interfaces/peppol-item.interface';
import { PeppolStatusDialogService } from '../../services/peppol-status-dialog.service';
import { PeppolCreateDocumentDialogComponent } from '../peppol-create-document-dialog/peppol-create-document-dialog.component';
import { PeppolDetailDialogComponent } from '../peppol-detail-dialog/peppol-detail-dialog.component';
import { PEPPOL_DETAIL_DIALOG_OPTIONS } from '../peppol-detail-dialog/peppol-detail-dialog.const';
import {
  PeppolDetailDialogData,
  PeppolDetailDialogResult,
} from '../peppol-detail-dialog/peppol-detail-dialog.interface';
import { peppolItemsPageEvents } from './store/peppol-items.events';
import { PeppolItemsStore } from './store/peppol-items.store';
import { PEPPOL_COLUMN_DEFINITIONS } from './const/columns.const';
import { PEPPOL_FILTER_DEFINITIONS } from './const/filters.const';
import {
  PeppolColumnKey,
  PeppolItemsControl,
} from './peppol-items.interface';
import { PeppolAction } from '../../interfaces/peppol-action.interface';

@Component({
  selector: 'wf-peppol-items',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    TranslateModule,
    SmartTableModule,
    FilterPanelComponent,
    SearchInputComponent,
    IconsModule,
    WuiTooltip,
  ],
  providers: [provideDispatcher(), PeppolItemsStore, PeppolStatusDialogService],
  templateUrl: './peppol-items.component.html',
  styleUrl: './peppol-items.component.scss',
  host: {
    class: 'wf-peppol-items',
  },
})
export class PeppolItemsComponent implements OnInit {
  private readonly translateSvc = inject(TranslateService);
  private readonly dialogSvc = inject(WfDialogService);
  private readonly store = inject(PeppolItemsStore);
  private readonly statusDialogSvc = inject(PeppolStatusDialogService);
  private readonly injector = inject(Injector);
  private readonly dispatch = injectDispatch(peppolItemsPageEvents);

  readonly control = input.required<PeppolItemsControl>();
  readonly storageKeyPrefix = input.required<string>();

  protected readonly search = this.store.search;
  protected readonly sort = this.store.sort;
  protected readonly loading = this.store.loading;
  protected readonly activeFiltersState = this.store.filters;

  protected readonly columns = computed(() =>
    this.buildColumns(this.control().columns, this.store.columnSizes()),
  );

  protected readonly dataSource = computed<DataSource<PeppolItem>>(() => ({
    data: this.store.items(),
    columns: this.columns(),
    pagination: new PaginationValueObject({
      pageNo: this.store.pagination().pageNo,
      pageSize: this.store.pagination().pageSize,
      itemsCount: this.store.totalItems(),
    }),
  }));

  protected readonly filterDefinitions = computed(() =>
    this.control().filters.map((key) => PEPPOL_FILTER_DEFINITIONS[key]),
  );

  protected readonly peppolStatusConfig = PEPPOL_STATUS_CONFIG;
  protected readonly peppolDirectionConfig = PEPPOL_DIRECTION_CONFIG;
  protected readonly peppolDocumentConfig = PEPPOL_DOCUMENT_CONFIG;

  ngOnInit(): void {
    this.dispatch.initialized({
      storageKeyPrefix: this.storageKeyPrefix(),
      scope: this.control().scope,
    });
  }

  protected onSearch(value: string): void {
    this.dispatch.searchChanged(value);
  }

  protected onFiltersChange(filterState: PanelFilterState): void {
    this.dispatch.filtersChanged(filterState);
  }

  protected changePage(value: Pagination): void {
    this.dispatch.paginationChanged(value);
  }

  protected changeSort(value: Sorting): void {
    this.dispatch.sortChanged(value);
  }

  protected sizesChange(event: ColumnSizes): void {
    this.dispatch.columnSizesChanged(event);
  }

  protected onRowAction(event: { event: string; data: PeppolItem }): void {
    const action = event.event as PeppolAction;

    if (action === PeppolAction.Reject) {
      this.openRejectDialog(event.data);
      return;
    }
    if (action === PeppolAction.CreateDocument) {
      this.openCreateDocumentDialog(event.data);
      return;
    }
    if (PEPPOL_STATUS_ACTIONS.includes(action)) {
      this.confirmStatusAction(action, event.data);
      return;
    }
    this.dispatch.actionInvoked({ action, item: event.data });
  }

  protected onRowClick(item: PeppolItem): void {
    const dialogRef = this.dialogSvc.open<
      PeppolDetailDialogComponent,
      PeppolDetailDialogData,
      PeppolDetailDialogResult
    >(PeppolDetailDialogComponent, {
      title: this.translateSvc.instant('PEPPOL.Detail.Title'),
      data: {
        messageId: item.id,
        canReject: this.control().canReject?.() ?? false,
      },
      ...PEPPOL_DETAIL_DIALOG_OPTIONS,
      injector: this.injector,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.createDocument) {
        this.openCreateDocumentDialog(item);
      }
    });
  }

  private confirmStatusAction(action: PeppolAction, item: PeppolItem): void {
    this.statusDialogSvc
      .confirm(action)
      .subscribe(() => this.dispatch.actionInvoked({ action, item }));
  }

  private openRejectDialog(item: PeppolItem): void {
    this.statusDialogSvc
      .collectRejection()
      .subscribe((request) => this.dispatch.rejectConfirmed({ item, request }));
  }

  private openCreateDocumentDialog(item: PeppolItem): void {
    this.dialogSvc
      .open(PeppolCreateDocumentDialogComponent, {
        title: this.translateSvc.instant('PEPPOL.CREATE_DOCUMENT_DIALOG.Title'),
        width: '600px',
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((typeId?: string) => {
        if (typeId) {
          this.dispatch.createDocumentConfirmed({ item, typeId });
        }
      });
  }

  private buildColumns(keys: PeppolColumnKey[], sizes: ColumnSizes | null) {
    const cols = keys.map((key) => {
      const col = PEPPOL_COLUMN_DEFINITIONS[key];
      return { ...col, label: this.translateSvc.instant(col.label) };
    });
    return setColumnSizes(cols, sizes ?? undefined);
  }
}
